import { useCallback, useMemo, useState } from "react";
import { Eye, LogOut } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { auth } from "../../api/firebaseApi";
import { ImpersonationAPI } from "../../api/impersonationApi";
import { useImpersonationClaims } from "../../hooks/useImpersonationClaims";
import { useLogOut } from "../../hooks/useLogOut";

export const ImpersonationBanner = () => {
  const { claims, isImpersonating } = useImpersonationClaims();
  const { onSubmitLogOut } = useLogOut();
  const [isStopping, setIsStopping] = useState(false);

  const email = auth.currentUser?.email ?? "";
  const targetLabel = email.trim() || auth.currentUser?.uid || "user";

  const startedByLabel = useMemo(() => {
    if (!claims) return "";
    return (claims.impersonatorEmail ?? "").trim() || claims.impersonatorUid;
  }, [claims]);

  const handleStop = useCallback(async () => {
    if (isStopping) return;
    setIsStopping(true);
    try {
      try {
        await ImpersonationAPI.end();
      } catch {
        // best-effort
      }

      // Ensure subsequent logins aren't forced into the impersonated tenant.
      auth.tenantId = null;
      toast.success("Impersonation ended", {
        description: "Please log in again.",
        duration: 5000
      });
      await onSubmitLogOut("impersonation_end");
    } finally {
      setIsStopping(false);
    }
  }, [isStopping, onSubmitLogOut]);

  if (!isImpersonating || !claims) return null;

  return (
    <div
      role="status"
      className="relative flex w-full items-center justify-center gap-3 bg-warn-soft py-2 pr-24 pl-4"
    >
      <div className="flex min-w-0 items-center justify-center gap-2 text-sm text-warn">
        <Eye className="size-3.5 shrink-0" aria-hidden />
        <span className="font-medium">Impersonation Active</span>
        <span className="truncate">
          {`You are logged in as ${targetLabel}. Started by ${startedByLabel}.`}
        </span>
      </div>
      <div className="absolute top-1/2 right-2 flex -translate-y-1/2 items-center gap-1">
        <Button
          variant="secondary"
          size="xs"
          disabled={isStopping}
          onClick={handleStop}
        >
          {isStopping ? <Spinner className="size-3.5" /> : <LogOut />}
          Stop
        </Button>
      </div>
    </div>
  );
};
